import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { PERMISSION_RESOURCES, PERMISSION_ACTIONS } from '../../constants/permissionConstants';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  useTheme,
  Tooltip,
  Badge,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  Skeleton,
  Avatar,
  AvatarGroup
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon,
  CalendarMonth as CalendarIcon,
  ExpandMore as ExpandMoreIcon,
  Task as TaskIcon,
  FiberManualRecord as DotIcon
} from '@mui/icons-material';
import { 
  format, 
  addMonths, 
  subMonths, 
  startOfMonth, 
  endOfMonth, 
  eachDayOfInterval, 
  isSameDay, 
  isToday, 
  isSameMonth,
  startOfWeek,
  endOfWeek,
  addWeeks,
  subWeeks,
  startOfDay,
  endOfDay,
  addDays,
  subDays
} from 'date-fns';

import useTaskStore from '../../stores/taskStore';
import useProjectStore from '../../stores/projectStore';
import { alpha } from '@mui/material';
import globalTaskCalendarService from '../../services/globalTaskCalendarService';

// Reuse calendar logic from ProjectCalendar
const getCalendarDays = (date) => {
  const monthStart = startOfMonth(date);
  const monthEnd = endOfMonth(date);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
  
  const startDay = monthStart.getDay();
  const endDay = 6 - monthEnd.getDay();
  
  const prevMonthDays = [];
  if (startDay > 0) {
    const prevMonthEnd = new Date(monthStart);
    prevMonthEnd.setDate(0);
    for (let i = startDay - 1; i >= 0; i--) {
      const day = new Date(prevMonthEnd);
      day.setDate(prevMonthEnd.getDate() - i);
      prevMonthDays.push(day);
    }
  }
  
  const nextMonthDays = [];
  if (endDay > 0) {
    const nextMonthStart = new Date(monthEnd);
    nextMonthStart.setDate(nextMonthStart.getDate() + 1);
    for (let i = 0; i < endDay; i++) {
      const day = new Date(nextMonthStart);
      day.setDate(nextMonthStart.getDate() + i);
      nextMonthDays.push(day);
    }
  }
  
  return [...prevMonthDays, ...daysInMonth, ...nextMonthDays];
};

const getWeekDays = (date) => {
  const weekStart = startOfWeek(date);
  const weekEnd = endOfWeek(date);
  return eachDayOfInterval({ start: weekStart, end: weekEnd });
};

const GlobalTaskCalendar = () => {
  const theme = useTheme();
  
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarDays, setCalendarDays] = useState([]);
  const [viewType, setViewType] = useState('month');
  const [isLoading, setIsLoading] = useState(false);
  const [tasksByDate, setTasksByDate] = useState({});
  const [summary, setSummary] = useState({});
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loadingMoreDays, setLoadingMoreDays] = useState(new Set());
  const [loadingDays, setLoadingDays] = useState(new Set());
  const [viewMode, setViewMode] = useState('all'); // 'assignee' | 'all' | 'owner'
  
  const { openTaskDrawer } = useTaskStore();
  const { fetchProjects, projects: storeProjects } = useProjectStore();
  
  // Calculate calendar days when current date or view type changes
  useEffect(() => {
    switch (viewType) {
      case 'month':
        setCalendarDays(getCalendarDays(currentDate));
        break;
      case 'week':
        setCalendarDays(getWeekDays(currentDate));
        break;
      case 'day':
        setCalendarDays([currentDate]);
        break;
      default:
        setCalendarDays(getCalendarDays(currentDate));
    }
  }, [currentDate, viewType]);
  
  // Fetch all accessible projects using store
  useEffect(() => {
    const loadProjects = async () => {
      try {
        await fetchProjects({ 
          limit: 100,
          status: 'active'
        });
      } catch (error) {
        console.error('Error fetching projects:', error);
      }
    };
    
    loadProjects();
  }, [fetchProjects]);
  
  // Update local projects state and selected projects when store updates
  useEffect(() => {
    if (storeProjects && storeProjects.length > 0) {
      setProjects(storeProjects);
      // Initially select all projects, filtering out any without valid IDs
      const validProjectIds = storeProjects
        .map(p => p._id)
        .filter(id => id && id !== 'undefined' && id !== 'null');
      setSelectedProjects(validProjectIds);
    }
  }, [storeProjects]);
  
  // Fetch summary first (lightweight)
  useEffect(() => {
    const fetchSummary = async () => {
      if (selectedProjects.length === 0) {
        setSummary({});
        return;
      }
      
      let startDate, endDate;
      switch (viewType) {
        case 'month':
          startDate = startOfMonth(currentDate);
          endDate = endOfMonth(currentDate);
          break;
        case 'week':
          startDate = startOfWeek(currentDate);
          endDate = endOfWeek(currentDate);
          break;
        case 'day':
          startDate = startOfDay(currentDate);
          endDate = endOfDay(currentDate);
          break;
        default:
          startDate = startOfMonth(currentDate);
          endDate = endOfMonth(currentDate);
      }
      
      try {
        const params = {
          projectIds: selectedProjects,
          startDate: format(startDate, 'yyyy-MM-dd'),
          endDate: format(endDate, 'yyyy-MM-dd'),
          viewMode
        };
        console.log('Calling getGlobalCalendarSummary with params:', params);
        const response = await globalTaskCalendarService.getGlobalCalendarSummary(params);
        console.log('Raw summary response:', response);
        console.log('Response type:', typeof response);
        console.log('Response has data?', response?.data !== undefined);
        console.log('Response.data value:', response?.data);
        
        // Check if response has data property or if it IS the data
        const summaryData = response.data || response;
        console.log('Final summary data:', summaryData);
        console.log('Sample day data:', Object.keys(summaryData).length > 0 ? summaryData[Object.keys(summaryData)[0]] : 'No data');
        setSummary(summaryData || {});
      } catch (error) {
        console.error('Error fetching summary:', error);
      }
    };
    
    console.log('Fetching summary with params:', {
      projectIds: selectedProjects,
      startDate: format(startOfMonth(currentDate), 'yyyy-MM-dd'),
      endDate: format(endOfMonth(currentDate), 'yyyy-MM-dd'),
      viewMode
    });
    fetchSummary();
    
    // Auto-load tasks for day view
    if (viewType === 'day') {
      const dateStr = format(currentDate, 'yyyy-MM-dd');
      if (!tasksByDate[dateStr]) {
        loadDayTasks(currentDate);
      }
    }
  }, [selectedProjects, currentDate, viewType, viewMode]);
  
  // Load tasks for a specific day when expanded
  const loadDayTasks = useCallback(async (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    
    if (tasksByDate[dateStr] || loadingDays.has(dateStr)) {
      return;
    }
    
    setLoadingDays(prev => new Set(prev).add(dateStr));
    
    try {
      const response = await globalTaskCalendarService.getGlobalCalendarDayTasks(dateStr, {
        projectIds: selectedProjects,
        limit: 50,  // Increased limit to show more tasks
        page: 1,  // Always start with page 1
        viewMode
      });
      
      console.log('Day tasks response:', response); // Debug log
      
      // Backend returns { success: true, data: { tasks: [...], pagination: {...} } }
      const dayData = response.data || response;
      
      setTasksByDate(prev => ({
        ...prev,
        [dateStr]: dayData
      }));
    } catch (error) {
      console.error('Error loading day tasks:', error);
    } finally {
      setLoadingDays(prev => {
        const next = new Set(prev);
        next.delete(dateStr);
        return next;
      });
    }
  }, [selectedProjects, tasksByDate, loadingDays, viewMode]);
  
  
  // Navigation functions
  const goToPrevious = () => {
    switch (viewType) {
      case 'month':
        setCurrentDate(subMonths(currentDate, 1));
        break;
      case 'week':
        setCurrentDate(subWeeks(currentDate, 1));
        break;
      case 'day':
        setCurrentDate(subDays(currentDate, 1));
        break;
    }
  };
  
  const goToNext = () => {
    switch (viewType) {
      case 'month':
        setCurrentDate(addMonths(currentDate, 1));
        break;
      case 'week':
        setCurrentDate(addWeeks(currentDate, 1));
        break;
      case 'day':
        setCurrentDate(addDays(currentDate, 1));
        break;
    }
  };
  
  const goToToday = () => {
    setCurrentDate(new Date());
  };
  
  const handleTaskClick = (task) => {
    openTaskDrawer(task);
  };
  
  const handleDayClick = useCallback((day) => {
    // Open task drawer with pre-filled date and no specific project
    const taskContext = {
      dueDate: format(day, 'yyyy-MM-dd'),
      status: 'to_do'
    };
    openTaskDrawer(taskContext);
  }, [openTaskDrawer]);
  
  // Get display format
  const getDisplayFormat = () => {
    switch (viewType) {
      case 'month':
        return format(currentDate, 'MMMM yyyy');
      case 'week':
        return `${format(startOfWeek(currentDate), 'MMM d')} - ${format(endOfWeek(currentDate), 'MMM d, yyyy')}`;
      case 'day':
        return format(currentDate, 'EEEE, MMMM d, yyyy');
      default:
        return format(currentDate, 'MMMM yyyy');
    }
  };
  
  // Get grid columns
  const getGridColumns = () => {
    switch (viewType) {
      case 'month':
        return 'repeat(7, 1fr)';
      case 'week':
        return 'repeat(7, 1fr)';
      case 'day':
        return '1fr';
      default:
        return 'repeat(7, 1fr)';
    }
  };
  
  // Get color for project
  const getProjectColor = (projectId) => {
    const project = projects.find(p => p._id === projectId);
    return project?.color || theme.palette.primary.main;
  };
  
  // Get priority color
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return theme.palette.error.main;
      case 'high': return theme.palette.warning.main;
      case 'medium': return theme.palette.info.main;
      case 'low': return theme.palette.success.main;
      default: return theme.palette.grey[500];
    }
  };
  
  return (
    <Box sx={{ p: 3 }}>
      {/* Page Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarIcon /> Global Task Calendar
        </Typography>
        <Typography variant="body1" color="textSecondary">
          View and manage tasks from all your projects in one unified calendar
        </Typography>
      </Box>
      
      {/* Calendar Controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton onClick={goToPrevious}>
              <ChevronLeftIcon />
            </IconButton>
            <Typography variant="h6" sx={{ minWidth: 200, textAlign: 'center' }}>
              {getDisplayFormat()}
            </Typography>
            <IconButton onClick={goToNext}>
              <ChevronRightIcon />
            </IconButton>
            <Button 
              variant="outlined" 
              size="small" 
              startIcon={<TodayIcon />}
              onClick={goToToday}
              sx={{ ml: 1 }}
            >
              Today
            </Button>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {/* View Mode */}
            <FormControl sx={{ minWidth: 150 }} size="small">
              <InputLabel>Show Tasks</InputLabel>
              <Select
                value={viewMode}
                label="Show Tasks"
                onChange={(e) => setViewMode(e.target.value)}
              >
                <MenuItem value="assignee">Assigned to Me</MenuItem>
                <MenuItem value="owner">Created by Me</MenuItem>
                <MenuItem value="all">All Tasks</MenuItem>
              </Select>
            </FormControl>
            
            {/* Project Filter */}
            <FormControl sx={{ minWidth: 200 }} size="small">
              <InputLabel>Projects</InputLabel>
              <Select
                multiple
                value={selectedProjects}
                onChange={(e) => setSelectedProjects(e.target.value)}
                label="Projects"
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.length === projects.length ? (
                      <Chip size="small" label="All Projects" />
                    ) : selected.length === 0 ? (
                      <Chip size="small" label="No Projects" />
                    ) : (
                      <>
                        {selected.slice(0, 2).map((value) => {
                          const project = projects.find(p => p._id === value);
                          return (
                            <Chip 
                              key={value} 
                              size="small" 
                              label={project?.name || 'Unknown'} 
                            />
                          );
                        })}
                        {selected.length > 2 && (
                          <Chip size="small" label={`+${selected.length - 2}`} />
                        )}
                      </>
                    )}
                  </Box>
                )}
              >
                <MenuItem 
                  onClick={(e) => {
                    e.preventDefault();
                    if (selectedProjects.length === projects.length) {
                      setSelectedProjects([]);
                    } else {
                      const validIds = projects
                        .map(p => p._id)
                        .filter(id => id && id !== 'undefined' && id !== 'null');
                      setSelectedProjects(validIds);
                    }
                  }}
                >
                  <em>{selectedProjects.length === projects.length ? 'Deselect All' : 'Select All'}</em>
                </MenuItem>
                {projects.map((project) => (
                  <MenuItem key={project._id} value={project._id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DotIcon sx={{ fontSize: 12, color: project.color || theme.palette.primary.main }} />
                      {project.name}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {/* View Type */}
            <FormControl sx={{ minWidth: 120 }} size="small">
              <InputLabel>View</InputLabel>
              <Select
                value={viewType}
                label="View"
                onChange={(e) => setViewType(e.target.value)}
              >
                <MenuItem value="month">Month</MenuItem>
                <MenuItem value="week">Week</MenuItem>
                <MenuItem value="day">Day</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Paper>
      
      {/* Calendar Grid */}
      <Paper sx={{ p: 2 }}>
        {/* Weekday Headers */}
        {viewType !== 'day' && (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', mb: 1 }}>
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => (
              <Box key={index} sx={{ textAlign: 'center', p: 1 }}>
                <Typography variant="subtitle2" fontWeight="medium">
                  {day}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
        
        {/* Calendar Days */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: getGridColumns(),
          gridAutoRows: 'minmax(120px, auto)',
          gap: 1,
          bgcolor: theme.palette.background.default
        }}>
          {calendarDays.map((day, index) => {
            const dateStr = format(day, 'yyyy-MM-dd');
            const daySummary = summary[dateStr];
            const dayTasks = tasksByDate[dateStr];
            const isCurrentMonth = viewType === 'month' ? isSameMonth(day, currentDate) : true;
            const isLoadingDay = loadingDays.has(dateStr);
            
            return (
              <Box 
                key={index} 
                sx={{ 
                  p: 1.5,
                  bgcolor: isCurrentMonth 
                    ? theme.palette.background.paper
                    : alpha(theme.palette.background.paper, 0.5),
                  border: isToday(day) 
                    ? `2px solid ${theme.palette.primary.main}`
                    : `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  opacity: isCurrentMonth ? 1 : 0.6,
                  minHeight: viewType === 'day' ? '400px' : '120px',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    boxShadow: 2,
                    transform: 'translateY(-2px)'
                  }
                }}
                onClick={() => handleDayClick(day)}
              >
                {/* Day Header */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  mb: 1
                }}>
                  <Typography 
                    variant={viewType === 'day' ? 'h6' : 'body2'} 
                    fontWeight={isToday(day) ? 'bold' : 'medium'}
                    color={isToday(day) ? 'primary' : 'textPrimary'}
                  >
                    {viewType === 'day' ? format(day, 'EEEE, MMMM d, yyyy') : format(day, 'd')}
                  </Typography>
                  
                  {daySummary?.totalCount > 0 && (
                    <Badge badgeContent={daySummary.totalCount} color="primary" max={99}>
                      <TaskIcon fontSize="small" />
                    </Badge>
                  )}
                </Box>
                
                {/* Priority indicators */}
                {daySummary?.priorities && (
                  <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
                    {Object.entries(daySummary.priorities).map(([priority, count]) => (
                      count > 0 && (
                        <Chip
                          key={priority}
                          size="small"
                          label={count}
                          sx={{
                            height: 20,
                            bgcolor: alpha(getPriorityColor(priority), 0.2),
                            color: getPriorityColor(priority),
                            fontWeight: 'bold',
                            fontSize: '0.7rem'
                          }}
                        />
                      )
                    ))}
                  </Box>
                )}
                
                {/* Tasks Section - Simple Progressive Loading */}
                {daySummary && daySummary.totalCount > 0 && viewType !== 'day' && (
                  <Box sx={{ mb: 1 }}>
                    {/* Always show tasks: either loaded ones or preview */}
                    {(() => {
                      const tasksToShow = dayTasks?.tasks || daySummary.preview || [];
                      const hasMoreToLoad = dayTasks ? 
                        (dayTasks.pagination?.hasMore || false) : 
                        (daySummary.totalCount > 3);
                      
                      return (
                        <>
                          {/* Show tasks */}
                          {tasksToShow.length > 0 && (
                            <Box sx={{ maxHeight: '200px', overflowY: 'auto', mb: 0.5 }}>
                              {tasksToShow.map((task, idx) => (
                                <Box 
                                  key={task._id || idx}
                                  sx={{
                                    fontSize: '0.75rem',
                                    color: 'text.primary',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    borderLeft: `2px solid ${getProjectColor(task.projectId?._id)}`,
                                    pl: 0.5,
                                    mb: 0.25,
                                    p: 0.25,
                                    cursor: 'pointer',
                                    '&:hover': {
                                      bgcolor: 'action.hover'
                                    }
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleTaskClick(task);
                                  }}
                                >
                                  • {task.title}
                                </Box>
                              ))}
                            </Box>
                          )}
                          
                          {/* Load More button */}
                          {hasMoreToLoad && (
                            <Button
                              size="small"
                              variant="text"
                              disabled={loadingDays.has(dateStr) || loadingMoreDays.has(dateStr)}
                              onClick={async (e) => {
                                e.stopPropagation();
                                
                                if (!dayTasks) {
                                  // First load - this will replace the preview with actual paginated data
                                  loadDayTasks(day);
                                } else if (dayTasks.pagination?.hasMore) {
                                  // Load next page - append to existing
                                  setLoadingMoreDays(prev => new Set(prev).add(dateStr));
                                  try {
                                    const nextPage = (dayTasks.pagination.page || 1) + 1;
                                    const response = await globalTaskCalendarService.getGlobalCalendarDayTasks(dateStr, {
                                      projectIds: selectedProjects,
                                      limit: 50,
                                      page: nextPage,
                                      viewMode
                                    });
                                    
                                    const newData = response.data || response;
                                    // When loading more pages, append to existing tasks
                                    setTasksByDate(prev => ({
                                      ...prev,
                                      [dateStr]: {
                                        tasks: [...(prev[dateStr]?.tasks || []), ...(newData.tasks || [])],
                                        pagination: newData.pagination
                                      }
                                    }));
                                  } catch (error) {
                                    console.error('Error loading more:', error);
                                  } finally {
                                    setLoadingMoreDays(prev => {
                                      const next = new Set(prev);
                                      next.delete(dateStr);
                                      return next;
                                    });
                                  }
                                }
                              }}
                              sx={{ fontSize: '0.7rem' }}
                            >
                              {loadingDays.has(dateStr) || loadingMoreDays.has(dateStr) ? 
                                'Loading...' : 
                                `Load More (${daySummary.totalCount - tasksToShow.length} remaining)`
                              }
                            </Button>
                          )}
                        </>
                      );
                    })()}
                  </Box>
                )}
                
                {/* Day view - show all tasks in scrollable area */}
                {viewType === 'day' && (
                  <Box sx={{ 
                    maxHeight: viewType === 'day' ? '350px' : '200px', 
                    overflowY: 'auto',
                    '&::-webkit-scrollbar': {
                      width: '4px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: theme.palette.divider,
                      borderRadius: '4px',
                    }
                  }}>
                    {isLoadingDay ? (
                      <>
                        <Skeleton variant="rectangular" height={30} sx={{ mb: 0.5 }} />
                        <Skeleton variant="rectangular" height={30} sx={{ mb: 0.5 }} />
                        <Skeleton variant="rectangular" height={30} />
                      </>
                    ) : dayTasks?.tasks ? (
                      dayTasks.tasks.map(task => (
                        <Box 
                          key={task._id}
                          sx={{
                            bgcolor: alpha(getProjectColor(task.project?._id || task.projectId?._id), 0.1),
                            borderLeft: `3px solid ${getProjectColor(task.project?._id || task.projectId?._id)}`,
                            p: 1,
                            borderRadius: 1,
                            mb: 0.5,
                            cursor: 'pointer',
                            '&:hover': {
                              bgcolor: alpha(getProjectColor(task.project?._id), 0.2)
                            }
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTaskClick(task);
                          }}
                        >
                          <Typography variant="caption" noWrap sx={{ fontWeight: 'medium' }}>
                            {task.title}
                          </Typography>
                          <Typography variant="caption" display="block" color="textSecondary" noWrap>
                            {task.project?.name || task.projectId?.name}
                          </Typography>
                          {(task.assignedTo || task.collaborators?.length > 0) && (
                            <AvatarGroup max={3} sx={{ mt: 0.5, justifyContent: 'flex-start' }}>
                              {task.assignedTo && (
                                <Avatar 
                                  key={task.assignedTo._id} 
                                  sx={{ width: 20, height: 20, fontSize: '0.7rem' }}
                                  src={task.assignedTo.avatar}
                                >
                                  {task.assignedTo.name?.charAt(0)}
                                </Avatar>
                              )}
                              {task.collaborators?.map(collab => (
                                <Avatar 
                                  key={collab.user._id} 
                                  sx={{ width: 20, height: 20, fontSize: '0.7rem' }}
                                  src={collab.user.avatar}
                                >
                                  {collab.user.name?.charAt(0)}
                                </Avatar>
                              ))}
                            </AvatarGroup>
                          )}
                        </Box>
                      ))
                    ) : null}
                    
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
        
        {/* Legend */}
        <Box sx={{ 
          mt: 2, 
          pt: 2, 
          borderTop: `1px solid ${theme.palette.divider}`, 
          display: 'flex', 
          gap: 2,
          flexWrap: 'wrap',
          alignItems: 'center'
        }}>
          <Typography variant="caption" color="textSecondary">
            Priority:
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {['urgent', 'high', 'medium', 'low'].map(priority => (
              <Box key={priority} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ 
                  width: 12, 
                  height: 12, 
                  bgcolor: getPriorityColor(priority), 
                  borderRadius: 0.5 
                }} />
                <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>
                  {priority}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default GlobalTaskCalendar;